import React from 'react';
import { Box, Card, CardActionArea, CardContent, Chip, Stack, Typography } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import RestaurantIcon from '@mui/icons-material/Restaurant';

import type { TrendOpportunity } from '../types';
import {
  getRunPresentationColor,
  getRunPresentationLabel,
  isLatestRunPresentation,
} from '../runPresentationUtils';
import { CARD_HOVER_SX, CARD_RADIUS, HEADING_SX, TREND_COLORS, getArticleImage, getFallbackArticleImage } from '../trendTheme';
import TrendScore from './TrendScore';

const OPPORTUNITY_TYPE_LABELS: Record<string, string> = {
  content_gap: 'Innehållsgap',
  communication_gap: 'Kommunikationsgap',
  portfolio_gap: 'Sortimentsgap',
  strong_match: 'Stark matchning',
  underused_asset: 'Underutnyttjad tillgång',
};

type TrendOpportunityCardProps = {
  opportunity: TrendOpportunity;
  onOpen?: (opportunity: TrendOpportunity) => void;
};

export default function TrendOpportunityCard({ opportunity, onOpen }: TrendOpportunityCardProps) {
  const linkedProductTitles = opportunity.linkedProducts.map((entry) => entry.title).slice(0, 3);
  const linkedRecipeTitles = opportunity.linkedRecipes.map((entry) => entry.title).slice(0, 3);
  const firstArticle = opportunity.linkedArticles?.[0];
  const imageUrl = firstArticle ? getArticleImage(firstArticle.heroImage, firstArticle.category) : null;
  const fallbackImageUrl = firstArticle ? getFallbackArticleImage(firstArticle.category) : null;

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: CARD_RADIUS,
        border: `1px solid ${TREND_COLORS.divider}`,
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        ...CARD_HOVER_SX,
      }}
    >
      <CardActionArea onClick={() => onOpen?.(opportunity)} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
        {imageUrl && (
          <Box sx={{ position: 'relative', pt: '40%', overflow: 'hidden' }}>
            <Box
              component="img"
              src={imageUrl}
              alt=""
              onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                if (fallbackImageUrl && event.currentTarget.src !== fallbackImageUrl) event.currentTarget.src = fallbackImageUrl;
              }}
              sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
        )}
        <CardContent sx={{ p: 2.5, flex: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 1.5 }}>
            <Typography
              variant="subtitle1"
              sx={{ ...HEADING_SX, fontSize: '0.9rem', fontWeight: 700, color: TREND_COLORS.navy }}
            >
              {opportunity.title}
            </Typography>
            <Chip
              size="small"
              label={(OPPORTUNITY_TYPE_LABELS[opportunity.type] || opportunity.type).toUpperCase()}
              sx={{
                bgcolor: TREND_COLORS.goldLight,
                color: TREND_COLORS.terracotta,
                fontWeight: 700,
                fontSize: '0.6rem',
                letterSpacing: '0.08em',
                borderRadius: 1,
                flexShrink: 0,
              }}
            />
          </Stack>
          {isLatestRunPresentation(opportunity.runPresentation) && (
            <Chip
              size="small"
              color={getRunPresentationColor(opportunity.runPresentation)}
              label={getRunPresentationLabel(opportunity.runPresentation)}
              sx={{ mb: 1.5, fontWeight: 700 }}
            />
          )}

          <Typography
            variant="body2"
            sx={{
              color: TREND_COLORS.textSecondary,
              lineHeight: 1.7,
              mb: 2,
              fontStyle: 'italic',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {opportunity.summary}
          </Typography>

          {(!!opportunity.dvhAngle || !!opportunity.foodserviceAngle) && (
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              {!!opportunity.dvhAngle && (
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <StorefrontIcon sx={{ fontSize: 18, color: TREND_COLORS.olive, mt: 0.25, flexShrink: 0 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: TREND_COLORS.textSecondary,
                      lineHeight: 1.65,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {opportunity.dvhAngle}
                  </Typography>
                </Stack>
              )}
              {!!opportunity.foodserviceAngle && (
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <RestaurantIcon sx={{ fontSize: 18, color: TREND_COLORS.terracotta, mt: 0.25, flexShrink: 0 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: TREND_COLORS.textSecondary,
                      lineHeight: 1.65,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {opportunity.foodserviceAngle}
                  </Typography>
                </Stack>
              )}
            </Stack>
          )}

          {(linkedProductTitles.length > 0 || linkedRecipeTitles.length > 0) && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              {linkedProductTitles.map((title) => (
                <Chip key={title} label={title} size="small" variant="outlined" sx={{ fontSize: '0.65rem', borderColor: TREND_COLORS.olive, color: TREND_COLORS.olive }} />
              ))}
              {linkedRecipeTitles.map((title) => (
                <Chip key={title} label={title} size="small" variant="outlined" sx={{ fontSize: '0.65rem', borderColor: TREND_COLORS.terracotta, color: TREND_COLORS.terracotta }} />
              ))}
            </Stack>
          )}

          <Box sx={{ mt: 'auto' }}>
            <TrendScore label="Säkerhet" value={opportunity.confidenceScore} />
            <Typography variant="caption" sx={{ display: 'block', mt: 1.25, fontWeight: 800, color: TREND_COLORS.navy }}>
              Öppna möjlighet
            </Typography>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
